import type { JobStatus, WorkRow } from '@workflow/contracts'
import type { StatusHue, StatusTone } from '@workflow/ui'
import { useMyHourAdjustments } from '@/lib/api/hooks'
import { formatTimestamp } from '@/lib/format'
import { JOB_STATUS, formatHours, partState } from '@/features/jobs/jobsStatus'

/**
 * A task is live while the player's part is open and the job still runs —
 * paused counts, since the work resumes. A part left open on a job the
 * studio completed, removed or cancelled is history.
 */
const LIVE_JOB_STATUSES: readonly JobStatus[] = ['published', 'in_progress', 'on_hold']
export const isLive = (row: WorkRow): boolean =>
  (row.completion === 'assigned' || row.completion === 'in_progress') && LIVE_JOB_STATUSES.includes(row.jobStatus)

/**
 * The state a row shows: the player's own part, unless the job's state
 * overrides it — a paused job reads Paused, and an open part on a closed
 * job reads as the job ended, so the word never promises work that is gone.
 */
export const rowState = (row: WorkRow): { label: string; tone: StatusTone; hue?: StatusHue } => {
  const open = row.completion === 'assigned' || row.completion === 'in_progress'
  if (open && row.jobStatus !== 'published' && row.jobStatus !== 'in_progress') return JOB_STATUS[row.jobStatus]
  return partState(row)
}

const assignedDay = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' })
const assignedTime = new Intl.DateTimeFormat('en', { hour: 'numeric', minute: '2-digit' })

/** "Oct 8, 2026 · 8:41 PM" — the day and the exact minute the work became theirs. */
export const assignedText = (iso: string): string => {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : `${assignedDay.format(date)} · ${assignedTime.format(date)}`
}

const shortDay = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' })

/** "Oct 8 · 8:41 PM" — the same minute for a narrow line; the year only when it is not this one. */
export const assignedShortText = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  if (date.getFullYear() !== new Date().getFullYear()) return assignedText(iso)
  return `${shortDay.format(date)} · ${assignedTime.format(date)}`
}

/** Corrections the studio made to the hours, with their reasons — shown only when there are any. */
export function HourAdjustments({ assignmentId, className }: { assignmentId: string; className?: string }) {
  const history = useMyHourAdjustments(assignmentId)
  if (!history.data?.length) return null
  return (
    <section className={className}>
      <p className="text-caption text-foreground">Hour Adjustments</p>
      <ul className="mt-1 space-y-1">
        {history.data.map((entry) => (
          <li key={entry.id} className="text-caption text-muted-foreground">
            {formatTimestamp(entry.createdAt)} · {formatHours(entry.from)} → {formatHours(entry.to)}. {entry.reason}
            {entry.evidenceUrl ? <> · <a href={entry.evidenceUrl} target="_blank" rel="noopener noreferrer" className="underline">Evidence</a></> : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
